import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  listarDocumentos,
  uploadDocumentos,
  downloadDocumento,
  excluirDocumento,
  editarDescricaoDocumento,
  type DocumentoListItem,
} from '../../services/documentoService';
import './Documentos.css';

interface DocumentosProps {
  projetoId: string;
}

interface ArquivoSelecionado {
  file: File;
  descricao: string;
  erro?: string;
}

const FORMATOS_ACEITOS = '.jpg,.jpeg,.png,.pdf,.docx,.xlsx,.dwg,.zip';
const EXTENSOES_ACEITAS = ['jpg', 'jpeg', 'png', 'pdf', 'docx', 'xlsx', 'dwg', 'zip'];
const TAMANHO_MAXIMO = 25 * 1024 * 1024; // 25MB
const MAX_ARQUIVOS = 10;

function formatarData(dataStr: string): string {
  const data = new Date(dataStr);
  const dia = String(data.getDate()).padStart(2, '0');
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const ano = data.getFullYear();
  const hora = String(data.getHours()).padStart(2, '0');
  const min = String(data.getMinutes()).padStart(2, '0');
  return `${dia}/${mes}/${ano} ${hora}:${min}`;
}

function truncarNome(nome: string, maxChars = 100): string {
  if (nome.length <= maxChars) return nome;
  return nome.substring(0, maxChars) + '…';
}

function formatarTamanho(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function obterExtensao(nomeArquivo: string): string {
  const partes = nomeArquivo.toLowerCase().split('.');
  return partes.length > 1 ? partes[partes.length - 1] : '';
}

function Documentos({ projetoId }: DocumentosProps) {
  const { user } = useAuth();
  const [documentos, setDocumentos] = useState<DocumentoListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Upload state
  const [uploadAberto, setUploadAberto] = useState(false);
  const [arquivosSelecionados, setArquivosSelecionados] = useState<ArquivoSelecionado[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [errosUpload, setErrosUpload] = useState<{ nomeArquivo: string; erro: string }[]>([]);
  const [sucessoUpload, setSucessoUpload] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Delete confirmation
  const [confirmExcluir, setConfirmExcluir] = useState<DocumentoListItem | null>(null);

  // Search/filter
  const [busca, setBusca] = useState('');
  const [buscaDebounced, setBuscaDebounced] = useState('');

  // Edit description
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [editDescricao, setEditDescricao] = useState('');

  const podeGerenciar = user?.tipo === 'Arquiteto' || user?.tipo === 'Assistente';

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setBuscaDebounced(busca), 300);
    return () => clearTimeout(timer);
  }, [busca]);

  const fetchDocumentos = async (termoBusca?: string) => {
    try {
      setLoading(true);
      const data = await listarDocumentos(projetoId, termoBusca || undefined);
      setDocumentos(data);
      setError('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar documentos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projetoId) fetchDocumentos(buscaDebounced);
  }, [projetoId, buscaDebounced]);

  const validarArquivo = (file: File): string | null => {
    const extensao = obterExtensao(file.name);
    if (!EXTENSOES_ACEITAS.includes(extensao)) {
      return `Formato não suportado (.${extensao}). Formatos aceitos: JPEG, PNG, PDF, DOCX, XLSX, DWG, ZIP.`;
    }
    if (file.size > TAMANHO_MAXIMO) {
      return `Arquivo excede o tamanho máximo de 25 MB (${formatarTamanho(file.size)}).`;
    }
    return null;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const novosArquivos: ArquivoSelecionado[] = [];
    const totalAtual = arquivosSelecionados.length;

    for (let i = 0; i < files.length && totalAtual + novosArquivos.length < MAX_ARQUIVOS; i++) {
      const file = files[i];
      const erroValidacao = validarArquivo(file);
      novosArquivos.push({
        file,
        descricao: '',
        erro: erroValidacao || undefined,
      });
    }

    if (totalAtual + files.length > MAX_ARQUIVOS) {
      setError(`Máximo de ${MAX_ARQUIVOS} arquivos por envio. Apenas os primeiros ${MAX_ARQUIVOS - totalAtual} foram adicionados.`);
    }

    setArquivosSelecionados([...arquivosSelecionados, ...novosArquivos]);
    setErrosUpload([]);
    setSucessoUpload('');

    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removerArquivo = (index: number) => {
    setArquivosSelecionados((prev) => prev.filter((_, i) => i !== index));
  };

  const atualizarDescricao = (index: number, descricao: string) => {
    setArquivosSelecionados((prev) =>
      prev.map((item, i) => (i === index ? { ...item, descricao } : item)),
    );
  };

  const validarDescricoes = (): boolean => {
    let valido = true;
    const atualizados = arquivosSelecionados.map((item) => {
      if (item.erro) return item; // Skip already invalid files
      const descTrimmed = item.descricao.trim();
      if (descTrimmed.length < 3 || descTrimmed.length > 255) {
        valido = false;
        return { ...item, erro: 'Descrição deve ter entre 3 e 255 caracteres.' };
      }
      return { ...item, erro: undefined };
    });
    setArquivosSelecionados(atualizados);
    return valido;
  };

  const handleUpload = async () => {
    if (!validarDescricoes()) return;

    const arquivosValidos = arquivosSelecionados.filter((a) => !a.erro);
    if (arquivosValidos.length === 0) {
      setError('Nenhum arquivo válido para enviar.');
      return;
    }

    setEnviando(true);
    setError('');
    setErrosUpload([]);
    setSucessoUpload('');

    try {
      const result = await uploadDocumentos(
        projetoId,
        arquivosValidos.map((a) => a.file),
        arquivosValidos.map((a) => a.descricao.trim()),
      );

      if (result.erros && result.erros.length > 0) {
        setErrosUpload(result.erros);
      }

      if (result.sucesso && result.sucesso.length > 0) {
        setSucessoUpload(`${result.sucesso.length} documento(s) enviado(s) com sucesso.`);
      }

      setArquivosSelecionados([]);
      setUploadAberto(false);
      await fetchDocumentos();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao enviar documentos.');
    } finally {
      setEnviando(false);
    }
  };

  const handleExcluir = async () => {
    if (!confirmExcluir) return;
    try {
      await excluirDocumento(projetoId, confirmExcluir.id);
      setConfirmExcluir(null);
      await fetchDocumentos();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir documento.');
      setConfirmExcluir(null);
    }
  };

  const handleDownload = (doc: DocumentoListItem) => {
    downloadDocumento(projetoId, doc.id);
  };

  const handleIniciarEdicao = (doc: DocumentoListItem) => {
    setEditandoId(doc.id);
    setEditDescricao(doc.descricao);
  };

  const handleSalvarDescricao = async () => {
    if (!editandoId) return;
    try {
      await editarDescricaoDocumento(projetoId, editandoId, editDescricao);
      setEditandoId(null);
      setEditDescricao('');
      await fetchDocumentos(buscaDebounced);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar descrição.');
    }
  };

  const handleCancelarEdicao = () => {
    setEditandoId(null);
    setEditDescricao('');
  };

  const cancelarUpload = () => {
    setUploadAberto(false);
    setArquivosSelecionados([]);
    setErrosUpload([]);
    setSucessoUpload('');
    setError('');
  };

  if (loading) {
    return (
      <div className="documentos">
        <p className="documentos__loading">Carregando documentos...</p>
      </div>
    );
  }

  return (
    <div className="documentos">
      <header className="documentos__header">
        <div className="documentos__header-info">
          <h2>Documentos</h2>
          <p className="documentos__subtitle">Repositório de documentos do projeto</p>
        </div>
        {podeGerenciar && (
          <button
            className="documentos__btn-upload"
            onClick={() => setUploadAberto(true)}
          >
            📎 Enviar Documentos
          </button>
        )}
      </header>

      <div className="documentos__busca">
        <input
          type="text"
          className="documentos__busca-input"
          placeholder="Pesquisar por descrição ou nome do arquivo..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          aria-label="Pesquisar documentos"
        />
        {busca && (
          <button
            className="documentos__busca-limpar"
            onClick={() => setBusca('')}
            aria-label="Limpar busca"
          >
            ✕
          </button>
        )}
      </div>

      {error && <p className="documentos__error">{error}</p>}
      {sucessoUpload && <p className="documentos__sucesso">{sucessoUpload}</p>}

      {errosUpload.length > 0 && (
        <div className="documentos__erros-upload">
          <p className="documentos__erros-upload-titulo">Alguns arquivos foram rejeitados:</p>
          <ul>
            {errosUpload.map((e, i) => (
              <li key={i}>
                <strong>{e.nomeArquivo}</strong>: {e.erro}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Upload section */}
      {uploadAberto && (
        <div className="documentos__upload-section">
          <div className="documentos__upload-header">
            <h3>Enviar Documentos</h3>
            <button
              className="documentos__upload-close"
              onClick={cancelarUpload}
              aria-label="Fechar upload"
            >
              ✕
            </button>
          </div>

          <div className="documentos__upload-body">
            <div className="documentos__upload-input-area">
              <input
                ref={fileInputRef}
                type="file"
                accept={FORMATOS_ACEITOS}
                multiple
                onChange={handleFileSelect}
                id="documento-file-input"
                className="documentos__file-input"
              />
              <label htmlFor="documento-file-input" className="documentos__file-label">
                📂 Selecionar Arquivos
              </label>
              <span className="documentos__upload-hint">
                Formatos: JPEG, PNG, PDF, DOCX, XLSX, DWG, ZIP — Máx: 25 MB por arquivo — Até 10 arquivos
              </span>
            </div>

            {arquivosSelecionados.length > 0 && (
              <div className="documentos__arquivos-lista">
                {arquivosSelecionados.map((item, index) => (
                  <div
                    key={index}
                    className={`documentos__arquivo-item ${item.erro ? 'documentos__arquivo-item--erro' : ''}`}
                  >
                    <div className="documentos__arquivo-info">
                      <span className="documentos__arquivo-nome">
                        {truncarNome(item.file.name, 50)}
                      </span>
                      <span className="documentos__arquivo-tamanho">
                        {formatarTamanho(item.file.size)}
                      </span>
                      <button
                        className="documentos__arquivo-remover"
                        onClick={() => removerArquivo(index)}
                        aria-label={`Remover ${item.file.name}`}
                      >
                        ✕
                      </button>
                    </div>
                    {item.erro ? (
                      <p className="documentos__arquivo-erro">{item.erro}</p>
                    ) : (
                      <div className="documentos__arquivo-descricao">
                        <input
                          type="text"
                          placeholder="Descrição do documento (3-255 caracteres)"
                          value={item.descricao}
                          onChange={(e) => atualizarDescricao(index, e.target.value)}
                          maxLength={255}
                          className="documentos__descricao-input"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="documentos__upload-actions">
              <button className="btn-secondary" onClick={cancelarUpload}>
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={handleUpload}
                disabled={enviando || arquivosSelecionados.filter((a) => !a.erro).length === 0}
              >
                {enviando ? 'Enviando...' : 'Enviar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Documents list */}
      {documentos.length === 0 ? (
        <div className="documentos__empty">
          <p>Não há documentos no repositório deste projeto</p>
        </div>
      ) : (
        <div className="documentos__table-container">
          <table className="documentos__table">
            <thead>
              <tr>
                <th>Documento</th>
                <th>Descrição</th>
                <th>Data</th>
                <th>Enviado por</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {documentos.map((doc) => (
                <tr key={doc.id} className="documentos__table-row">
                  <td className="documentos__table-cell-nome" title={doc.nomeOriginal}>
                    {truncarNome(doc.nomeOriginal)}
                  </td>
                  <td className="documentos__table-cell-descricao">
                    {editandoId === doc.id ? (
                      <div className="documentos__edit-descricao">
                        <input
                          type="text"
                          className="documentos__edit-input"
                          value={editDescricao}
                          onChange={(e) => setEditDescricao(e.target.value)}
                          maxLength={255}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSalvarDescricao();
                            if (e.key === 'Escape') handleCancelarEdicao();
                          }}
                        />
                        <button className="documentos__edit-btn--salvar" onClick={handleSalvarDescricao} title="Salvar">✓</button>
                        <button className="documentos__edit-btn--cancelar" onClick={handleCancelarEdicao} title="Cancelar">✕</button>
                      </div>
                    ) : (
                      <span
                        className={podeGerenciar ? 'documentos__descricao-editavel' : ''}
                        onClick={() => podeGerenciar && handleIniciarEdicao(doc)}
                        title={podeGerenciar ? 'Clique para editar' : undefined}
                      >
                        {doc.descricao}
                      </span>
                    )}
                  </td>
                  <td className="documentos__table-cell-data">{formatarData(doc.enviadoEm)}</td>
                  <td className="documentos__table-cell-uploader">{doc.enviadoPor.nome}</td>
                  <td>
                    <div className="documentos__table-actions">
                      <button
                        className="documentos__action-btn"
                        onClick={() => handleDownload(doc)}
                        aria-label={`Baixar ${doc.nomeOriginal}`}
                        title="Baixar"
                      >
                        ⬇️
                      </button>
                      {podeGerenciar && (
                        <button
                          className="documentos__action-btn documentos__action-btn--editar"
                          onClick={() => handleIniciarEdicao(doc)}
                          aria-label={`Editar descrição de ${doc.nomeOriginal}`}
                          title="Editar descrição"
                        >
                          ✏️
                        </button>
                      )}
                      {podeGerenciar && (
                        <button
                          className="documentos__action-btn documentos__action-btn--excluir"
                          onClick={() => setConfirmExcluir(doc)}
                          aria-label={`Excluir ${doc.nomeOriginal}`}
                          title="Excluir"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Confirm Delete Modal */}
      {confirmExcluir && (
        <div className="documentos__modal-overlay" onClick={() => setConfirmExcluir(null)}>
          <div className="documentos__confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Confirmar Exclusão</h3>
            <p>
              Deseja excluir o documento <strong>{truncarNome(confirmExcluir.nomeOriginal, 60)}</strong>?
            </p>
            <p className="documentos__confirm-aviso">Esta ação não pode ser desfeita.</p>
            <div className="documentos__confirm-actions">
              <button className="btn-secondary" onClick={() => setConfirmExcluir(null)}>
                Cancelar
              </button>
              <button className="btn-primary documentos__btn-excluir" onClick={handleExcluir}>
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Documentos;
